import { type HTMLAttributes, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import {
    BorderRadiusMedium,
    BorderRadiusSmall,
    ColorNeutralDarkGrey,
    FontBody2WeightLight,
    FontLineHeightNone,
    FontSize12,
    FontSize18,
    FontSize28,
    FontSize48,
    Spacing2,
    Spacing4,
    Spacing6,
    Spacing8,
    Spacing12,
    Spacing16,
    Spacing24,
    Spacing32
} from 'autogen/design-tokens/tokens';
import { useJavascriptRequestDataModelQuery } from 'autogen/swagger/all/hooks';
import { Body, Typography } from 'components/generic/typography';
import { em } from 'modules/helpers/style';
import ThemeProvider from 'theming/ThemeProvider';
import { breakpoint, sizes } from 'variables';
import { calculateTimeLeft, generateNumbers, generateTranslations, timeDifference } from './shared/helpers';
import type { ICountdown } from './shared/types';

const Wrapper = styled.span<Pick<ICountdown, 'sizeType'>>`
    display: inline-flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${({ theme }) => theme.Spacing8};
    width: 100%;
    line-height: ${FontLineHeightNone};
    color: ${({ theme }) => theme.v2.color.text.primary};

    ${({ sizeType }) =>
        sizeType === 'M' &&
        css`
            background: ${({ theme }) => theme.v2.color.bg.tertiary};
            border-radius: ${BorderRadiusMedium};
            padding: ${Spacing16} ${Spacing32};

            ${breakpoint.up(sizes.md)} {
                max-width: 280px;
            }
        `}

    ${breakpoint.up(sizes.md)} {
        width: unset;
    }
`;

const StyledLabel = styled(Typography.Label)<Pick<ICountdown, 'sizeType'>>`
    line-height: ${FontLineHeightNone};
    text-align: center;
    margin-bottom: ${({ theme }) => theme.Spacing8};
`;

const Counter = styled.span`
    display: flex;
    align-items: flex-start;
`;

const Unit = styled.span<Pick<ICountdown, 'sizeType'>>`
    ${({ sizeType }) =>
        (
            sizeType === 'XS' &&
                css`
                display: flex;
                align-items: flex-end;

                & ~ & {
                    margin-left: ${Spacing4};
                }
            `
        ) ||
        (
            sizeType !== 'XS' &&
                css`
                display: flex;
                flex-direction: column;
                gap: ${({ theme }) => theme.Spacing8};
                text-align: center;
            `
        )}
`;

const NumberBlock = styled.span`
    display: flex;
`;

// biome-ignore lint/suspicious/noShadowRestrictedNames: <explanation>
const Number = styled.span<Pick<ICountdown, 'sizeType'>>`
    ${({ sizeType }) =>
        (sizeType === 'XS' &&
            css`
                width: ${em(9)}; // prevents size change
                text-align: center;
            `) ||
        (sizeType === 'M' &&
            css`
                width: 20px;
                font-size: ${FontSize18};
                padding: ${Spacing6} 0;
                border-radius: 2px;
                text-align: center;
                background: ${({ theme }) => theme.v2.color.bg.tertiary};
                margin: 0 ${Spacing2};
            `) ||
        (
            sizeType === 'XL' &&
                css`
                width: 35px;
                font-size: ${FontSize28};
                font-weight: ${FontBody2WeightLight};
                padding: ${Spacing12} 0;
                border-radius: ${BorderRadiusSmall};
                text-align: center;
                background: ${({ theme }) => theme.v2.color.bg.tertiary};
                margin: 0 ${Spacing2};

                ${breakpoint.up(sizes.md)} {
                    width: 45px;
                    font-size: ${FontSize48};
                }

                @media (max-width: 375px) {
                    width: 27px;
                    font-size: ${FontSize18};
                }
            `
        )}
`;

const TimeUnit = styled.span<Pick<ICountdown, 'sizeType'>>`
    ${({ sizeType }) =>
        (
            sizeType === 'M' &&
                css`
                font-size: ${FontSize12};
            `
        ) ||
        (
            sizeType === 'XL' &&
                css`
                font-size: ${FontSize12};
            `
        )}
`;

const Seperator = styled.span<Pick<ICountdown, 'sizeType'>>`
    ${({ sizeType }) =>
        (sizeType === 'XS' &&
            css`
                display: none;
            `) ||
        (sizeType === 'M' &&
            css`
                margin: ${Spacing8} ${Spacing2} 0;
            `) ||
        (
            sizeType === 'XL' &&
                css`
                margin: ${({ theme }) => `${theme.Spacing12} ${theme.Spacing2}`};
                color: ${ColorNeutralDarkGrey};
                font-size: ${FontSize28};
                font-weight: ${FontBody2WeightLight};

                ${breakpoint.up(sizes.md)} {
                    margin-top: ${({ theme }) => theme.Spacing20};
                }

                @media (max-width: 375px) {
                    font-size: ${FontSize18};
                }
            `
        )}
`;

type CountdownProps = HTMLAttributes<HTMLDivElement> & {
    countdown: ICountdown;
};

export default function Countdown(props: CountdownProps) {
    const { countdown, className } = props;
    const { data } = useJavascriptRequestDataModelQuery({ enabled: false });
    const {
        finishDateTime,
        currentTime = data?.websiteTime.load,
        sizeType,
        text,
        showTimeUnits,
        dayToHourThresholdMilliseconds,
        displayType,
        designThemeName
    } = countdown;

    const { t } = useTranslation();

    const updateInterval = 1000;
    const startDate = currentTime;

    let millisecondsLeft = useMemo(() => timeDifference({ startDate, finishDateTime }), [startDate, finishDateTime]);
    const initialTime = useMemo(
        () => calculateTimeLeft(millisecondsLeft, displayType, dayToHourThresholdMilliseconds),
        []
    );
    const [time, setTime] = useState(initialTime);
    const { displayFormat } = time;

    const translationTimeStrings = generateTranslations(sizeType);
    const numbers = generateNumbers(sizeType, time);

    useEffect(() => {
        const interval = setInterval(() => {
            const timeObject = calculateTimeLeft(millisecondsLeft, displayType, dayToHourThresholdMilliseconds);

            requestAnimationFrame(() => setTime(timeObject));

            if (millisecondsLeft <= 0) {
                clearInterval(interval);
            } else {
                millisecondsLeft -= updateInterval;
            }
        }, updateInterval);

        return () => clearInterval(interval);
    }, [millisecondsLeft, dayToHourThresholdMilliseconds, displayType]);

    return (
        <ThemeProvider theme={designThemeName}>
            <Wrapper className={className} sizeType={sizeType}>
                {sizeType === 'M' && !!text && (
                    <StyledLabel size="M" strong forwardedAs="span" sizeType={sizeType}>
                        {text}
                    </StyledLabel>
                )}
                <Counter>
                    {displayFormat === 'days' && numbers.daysArray && (
                        <>
                            <Unit suppressHydrationWarning sizeType={sizeType}>
                                <NumberBlock>
                                    {numbers.daysArray.map((number, i) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <Number key={i} suppressHydrationWarning sizeType={sizeType}>
                                            {number}
                                        </Number>
                                    ))}
                                </NumberBlock>
                                {showTimeUnits && (
                                    <TimeUnit suppressHydrationWarning sizeType={sizeType}>
                                        {t(translationTimeStrings.days)}
                                    </TimeUnit>
                                )}
                            </Unit>
                            <Seperator sizeType={sizeType}>:</Seperator>
                        </>
                    )}
                    {numbers.hoursArray && (
                        <>
                            <Unit suppressHydrationWarning sizeType={sizeType}>
                                <NumberBlock>
                                    {numbers.hoursArray.map((number, i) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <Number key={i} suppressHydrationWarning sizeType={sizeType}>
                                            {number}
                                        </Number>
                                    ))}
                                </NumberBlock>
                                {showTimeUnits && (
                                    <TimeUnit suppressHydrationWarning sizeType={sizeType}>
                                        {t(translationTimeStrings.hours)}
                                    </TimeUnit>
                                )}
                            </Unit>
                            <Seperator sizeType={sizeType}>:</Seperator>
                        </>
                    )}
                    {numbers.minutesArray && (
                        <>
                            <Unit suppressHydrationWarning sizeType={sizeType}>
                                <NumberBlock>
                                    {numbers.minutesArray.map((number, i) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <Number key={i} suppressHydrationWarning sizeType={sizeType}>
                                            {number}
                                        </Number>
                                    ))}
                                </NumberBlock>
                                {showTimeUnits && (
                                    <TimeUnit suppressHydrationWarning sizeType={sizeType}>
                                        {t(translationTimeStrings.minutes)}
                                    </TimeUnit>
                                )}
                            </Unit>
                            <Seperator sizeType={sizeType}>:</Seperator>
                        </>
                    )}
                    <Unit sizeType={sizeType}>
                        <NumberBlock>
                            {numbers.secondsArray.map((number, i) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <Number key={i} suppressHydrationWarning sizeType={sizeType}>
                                    {number}
                                </Number>
                            ))}
                        </NumberBlock>
                        {showTimeUnits && (
                            <TimeUnit suppressHydrationWarning sizeType={sizeType}>
                                {t(translationTimeStrings.seconds)}
                            </TimeUnit>
                        )}
                    </Unit>
                </Counter>
            </Wrapper>
        </ThemeProvider>
    );
}
